import { DataTable, DataTableHTML } from '@/components/shared/data-table';
import { DatePickerWithRange } from '@/components/shared/date-picker-with-range';
import { LastUpdatedInfo } from '@/components/shared/last-updated-info';
import { PrintReportButton } from '@/components/shared/print-report-button';
import { SelectCompanyLocations } from '@/components/shared/select-company-locations';
import { Button } from '@/components/shared/shadcn-ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/shared/shadcn-ui/dropdown-menu';
import { Skeleton } from '@/components/shared/shadcn-ui/skeleton';
import { columns } from '@/config/restaurant/reports/ingredients-by-location';
import { useDatePickerRangeState } from '@/hooks/use-date-picker-range-state';
import TableSizeSyncProvider from '@/providers/TableSizeSyncProvider';
import { repeat } from '@/utils/helpers';
import {
  SHIFT,
  useAuthUserRestaurant,
  useGetAllRestaurantCompaniesQuery,
  useGetIngredientsByLocationReportQuery,
} from '@durma-soft/gros-sdk';
import {
  RestaurantShift,
  ShortRestaurantLocation,
} from '@durma-soft/gros-sdk/dist/types/restaurants';
import { SortingState } from '@tanstack/react-table';
import { ChevronDown } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

import print from '@/lib/print/print';
import { format, isEqual } from 'date-fns';

const IngredientsByLocation = () => {
  const { t } = useTranslation();
  const restaurant = useAuthUserRestaurant();
  const memoizedColumns = useMemo(() => columns(t), [t]);
  const { from, to, setDateRange } = useDatePickerRangeState();

  const [sorting, setSorting] = useState<SortingState>([]);
  const [shiftsDropdownOpen, setShiftsDropdownOpen] = useState(false);
  const [companyLocations, setCompanyLocations] = useState<
    ShortRestaurantLocation[]
  >([]);
  const [ingredientTypeDropdownOpen, setIngredientTypeDropdownOpen] =
    useState(false);
  const [ingredientType, setIngredientType] = useState<
    'side_dish' | 'main_dish' | 'all'
  >('side_dish');
  const [selectedShifts, setSelectedShifts] = useState<RestaurantShift[]>([]);

  const onUpdateCompanyLocations = (
    newCompanyLocations: ShortRestaurantLocation[],
  ) => {
    setCompanyLocations(newCompanyLocations);
  };

  const {
    data: companiesAndGroups,
    isLoading: isLoadingCompanies,
    isSuccess: isSuccessCompanies,
  } = useGetAllRestaurantCompaniesQuery(restaurant.id);

  const {
    data: ingredientsByLocation = [],
    isLoading: isLoadingIngredientsByLocation,
    isSuccess: isSuccessIngredientsByLocation,
    refetch,
    dataUpdatedAt,
    isRefetching,
  } = useGetIngredientsByLocationReportQuery({
    restaurant_id: restaurant.id,
    start_date: from,
    end_date: to,
    company_location_ids: companyLocations.length
      ? companyLocations.map(({ id }) => id)
      : [],
  });

  const handleRefetch = async () => {
    await refetch();
  };

  const filteredData = useMemo(() => {
    if (!ingredientsByLocation) return [];

    return ingredientsByLocation.map((location) => ({
      ...location,
      ingredients:
        ingredientType === 'all'
          ? location.ingredients
          : ingredientType === 'side_dish'
          ? location.ingredients.filter((ingredient) => ingredient.is_side_dish)
          : location.ingredients.filter(
              (ingredient) => !ingredient.is_side_dish,
            ),
    }));
  }, [ingredientsByLocation, ingredientType]);

  return (
    <>
      <Helmet>
        <title>{t('reports.ingredients-by-location.title')}</title>
      </Helmet>
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold md:text-2xl">
          {t('reports.ingredients-by-location.title')}
        </h1>
        {(isLoadingCompanies || isLoadingIngredientsByLocation) && (
          <Skeleton className="h-10 w-52" />
        )}
        {isSuccessCompanies && isSuccessIngredientsByLocation && (
          <LastUpdatedInfo
            dataUpdatedAt={dataUpdatedAt}
            isRefetching={isRefetching}
            handleRefetch={handleRefetch}
          />
        )}
      </div>
      <div className="flex flex-wrap justify-between gap-5 mt-5 mb-5">
        <div className="flex flex-col xl:flex-row gap-5">
          <DatePickerWithRange
            date={{
              from: new Date(from),
              to: new Date(to),
            }}
            setDate={([from, to]) => {
              setDateRange({ from, to });
            }}
          />
          <DropdownMenu
            open={ingredientTypeDropdownOpen}
            onOpenChange={setIngredientTypeDropdownOpen}
          >
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="gap-2 font-normal print:hidden flex justify-between w-[200px]"
              >
                {t('ingredient.choose-type')}
                <span>
                  <ChevronDown size={16} className="opacity-50" />
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-[200px]">
              {['all', 'main_dish', 'side_dish'].map((type) => (
                <DropdownMenuCheckboxItem
                  key={type}
                  checked={ingredientType === type}
                  onSelect={(event) => {
                    event.preventDefault();
                    setIngredientType(
                      type as 'all' | 'main_dish' | 'side_dish',
                    );
                  }}
                >
                  {type === 'all'
                    ? t('ingredient.all')
                    : type === 'side_dish'
                    ? t('ingredient.side-dish')
                    : t('ingredient.main-dish')}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu
            open={shiftsDropdownOpen}
            onOpenChange={setShiftsDropdownOpen}
          >
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="gap-2 font-normal print:hidden flex justify-between w-[200px]"
              >
                {t('common.choose-shifts')}
                <span>
                  <ChevronDown size={16} className="opacity-50" />
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-[200px]">
              {['1', '2', '3'].map((shift) => (
                <DropdownMenuCheckboxItem
                  key={shift}
                  checked={selectedShifts.includes(shift as RestaurantShift)}
                  onSelect={(event) => {
                    event.preventDefault();
                    setSelectedShifts((prev) =>
                      prev.includes(shift as RestaurantShift)
                        ? prev.filter((s) => s !== shift)
                        : [...prev, shift as RestaurantShift],
                    );
                  }}
                >
                  {shift}. {t('menu.shift')}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <SelectCompanyLocations
            companies={companiesAndGroups?.companies || []}
            groups={companiesAndGroups?.groups || []}
            isLoading={isLoadingCompanies}
            selectedCompanyLocations={companyLocations}
            onUpdateCompanyLocations={onUpdateCompanyLocations}
          />
        </div>
        <div>
          <PrintReportButton
            disabled={isLoadingCompanies || isLoadingIngredientsByLocation}
            onPrint={(fontScale) =>
              print({
                fontScale,
                printInSeparatePage: false,
                content: (
                  <div>
                    <div className="flex-between">
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <h1 style={{ margin: 0 }}>
                          {t('reports.ingredients.title')}
                        </h1>
                        <p style={{ margin: 0 }}>
                          {t('ingredient.type')}:{' '}
                          {ingredientType === 'all'
                            ? t('ingredient.all')
                            : ingredientType === 'side_dish'
                            ? t('ingredient.side-dish')
                            : t('ingredient.main-dish')}
                        </p>
                        <p>
                          {format(from, 'dd.MM.yyyy')}
                          {!isEqual(from, to) &&
                            ` - ${format(to, 'dd.MM.yyyy')}`}
                        </p>
                      </div>
                      <div className="flex-row">
                        <p>{t('common.data-updated-at')}</p>
                        <span>&nbsp;</span>
                        <p>
                          <span> {format(dataUpdatedAt, 'dd.MM.yyyy')}</span>
                          <span> {t('common.in')} </span>
                          <span> {format(dataUpdatedAt, 'HH:mm:ss')}</span>
                        </p>
                      </div>
                    </div>
                    <div>
                      {isSuccessIngredientsByLocation &&
                        filteredData.map(({ id, name, ingredients }) => {
                          if (!ingredients.length) return null;
                          return (
                            <div
                              key={id}
                              className="flex flex-col gap-4 keep-together"
                            >
                              <h3 className="text-xl font-medium">{name}</h3>
                              <DataTableHTML
                                data={ingredients}
                                columns={memoizedColumns}
                                sorting={sorting}
                                setSorting={setSorting}
                                columnVisibility={{
                                  shift_1: selectedShifts.includes(SHIFT.FIRST),
                                  shift_2: selectedShifts.includes(
                                    SHIFT.SECOND,
                                  ),
                                  shift_3: selectedShifts.includes(SHIFT.THIRD),
                                }}
                              />
                            </div>
                          );
                        })}
                    </div>
                  </div>
                ),
              })
            }
          />
        </div>
      </div>
      <div className="flex flex-col gap-6">
        {(isLoadingCompanies || isLoadingIngredientsByLocation) && (
          <div className="flex flex-col gap-10">
            {repeat(3).map((index) => (
              <div key={index}>
                <Skeleton className="w-full rounded-md h-60" />
              </div>
            ))}
          </div>
        )}
        {isSuccessIngredientsByLocation && !ingredientsByLocation.length && (
          <p className="self-center">{t('reports.no-data')}</p>
        )}
        {isSuccessIngredientsByLocation && (
          <TableSizeSyncProvider>
            {filteredData.map(({ id, name, ingredients }, index) => {
              if (
                !ingredients.length &&
                (ingredientType === 'side_dish' ||
                  ingredientType === 'main_dish')
              )
                return null;
              return (
                <div
                  key={id}
                  className="flex flex-col gap-4 print-in-separate-page"
                >
                  <h3 className="text-xl font-medium">{name}</h3>
                  <DataTable
                    data={ingredients}
                    sorting={sorting}
                    setSorting={setSorting}
                    columns={memoizedColumns}
                    isSyncReference={index === 0}
                    columnVisibility={{
                      shift_1: selectedShifts.includes(SHIFT.FIRST),
                      shift_2: selectedShifts.includes(SHIFT.SECOND),
                      shift_3: selectedShifts.includes(SHIFT.THIRD),
                    }}
                  />
                </div>
              );
            })}
          </TableSizeSyncProvider>
        )}
      </div>
    </>
  );
};

export default IngredientsByLocation;
